import { useCallback, useState } from 'react';
import { CaretDown, CaretUp, Warning } from '@phosphor-icons/react';

import {
  groupDisplayBudgetEur,
  groupExpensesHeaderAmounts,
  groupSpentVsBudgetStatus,
  groupSpentVsTargetPct,
} from '../budget/budgetSpendUtils';
import { CardErrorState } from '../common/CardErrorState';
import { GroupProgressTrack } from '../common/GroupProgressTrack';
import {
  TooltipContent,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
} from '../ui/Tooltip';
import pt from '../../locales/pt';
import { cn } from '../../utils/cn';
import { categoriesForExpensesByGroup, groupKey } from '../budget/budgetGroupCategories';
import { formatEUR } from '../../utils/money';
import { formatGuidedFlowTemplate } from '../../utils/guidedFlowUtils';

const DEFAULT_GROUP_COLOR = '#7C9E6F';

/** Ícone de aviso — categoria/grupo com gasto acima do orçamento; clique mostra detalhe. */
function BudgetWarningIcon({ tooltip, ariaLabel }) {
  return (
    <TooltipProvider delayDuration={150}>
      <TooltipRoot>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={ariaLabel}
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-danger-fg"
          >
            <Warning className="h-4 w-4" weight="fill" aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </TooltipRoot>
    </TooltipProvider>
  );
}

/**
 * Linha de categoria dentro do grupo aberto — `.pen` `ds/row/category-progress`
 * (`r40CS`): vertical gap `$space-2`; topo `space_between` com nome
 * (`$type-body-sm` normal, `$fg-card` — `$fg-muted` quando esbatido) e valor
 * (`$type-chip` 600, `$fg-muted`); faixa `ds/feedback/progress-bar` h 4.
 */
function CategorySpendRow({ name, spent, budget, color, status, dimmed = false, testId }) {
  const isExceeded = status === 'exceeded';
  return (
    <li className="flex min-w-0 flex-col gap-space-2" data-testid={testId}>
      <div className="flex min-w-0 items-center justify-between gap-space-2">
        <span className="flex min-w-0 flex-1 items-center gap-space-1">
          <span
            className={cn(
              'type-body-sm min-w-0 truncate',
              dimmed ? 'text-muted-foreground' : 'text-card-foreground',
            )}
          >
            {name}
          </span>
          {isExceeded ? (
            <BudgetWarningIcon
              ariaLabel={formatGuidedFlowTemplate(pt.expenses.byGroup.categoryOverBudget, {
                amount: formatEUR(Math.max(0, Number(spent) - Number(budget))),
                percent: budget ? Math.round((Number(spent) / Number(budget)) * 100) : 0,
                budget: formatEUR(budget),
              })}
              tooltip={formatGuidedFlowTemplate(pt.expenses.byGroup.categoryOverBudget, {
                amount: formatEUR(Math.max(0, Number(spent) - Number(budget))),
                percent: budget ? Math.round((Number(spent) / Number(budget)) * 100) : 0,
                budget: formatEUR(budget),
              })}
            />
          ) : null}
        </span>
        <span className="type-chip tnum shrink-0 text-muted-foreground">
          {formatEUR(spent)}
          {budget != null ? ` / ${formatEUR(budget)}` : ''}
        </span>
      </div>
      <GroupProgressTrack
        percentage={budget != null ? (Number(spent) / Number(budget)) * 100 : 100}
        color={color}
        size="category"
        gradient
        dimmed={dimmed}
        ariaLabel={name}
      />
    </li>
  );
}

/**
 * Cartão de grupo de despesa — `.pen` `ds/card/expense-group--fechado` (`wAsPP`)
 * e `--aberto` (`Bv3BO`), gramática da família group-card:
 *   marcador → cabeçalho com total → faixa de referência → itens → pé.
 *
 * Tokens do `.pen`:
 *   card    `$surface-card` · `$radius-ds-lg` · stroke `$border`
 *   header  padding `[$space-4, $space-5]` · gap `$space-3` · center
 *     dot   ellipse 8×8 · cor do grupo
 *     n     `$font-display` `$type-title-card` 600 · `$fg-card`
 *     a     `$font-display` `$type-body-sm` 600 · `$fg-card`
 *     chevron 16 · `$fg-muted`
 *   bar-wrap padding `[0, $space-5, $space-4, $space-5]` · track h 8
 *   divider rectângulo 1px `$border`
 *   categories vertical gap `$space-3` · padding `[$space-3, $space-5, $space-4, $space-5]`
 *   empty-slot padding `[$space-2-5, $space-4]` · `$type-chip` normal `$fg-muted`
 */
function ExpensesGroupSection({ group, hasIncome, expanded, onToggle, isError, onRetry }) {
  const categories = categoriesForExpensesByGroup(group);
  const groupLabel = group.is_pay_yourself_first
    ? pt.expenses.byGroup.pyfGroupLabel
    : group.name || pt.categories.form.noGroup;
  const color = group.color || DEFAULT_GROUP_COLOR;
  const isReserved = Boolean(group.is_pay_yourself_first);
  const badgeStatus = isReserved ? 'ok' : groupSpentVsBudgetStatus(group);
  const isAtRisk = !isReserved && badgeStatus && badgeStatus !== 'ok';
  const exceededCount = categories.filter((cat) => cat.status === 'exceeded').length;
  const header = groupExpensesHeaderAmounts(group);
  const limit = groupDisplayBudgetEur(group);
  const showBar = hasIncome && limit != null;

  // usar % contra alocação do método; fallback para spent_pct da API
  const targetPct = groupSpentVsTargetPct(group);
  const barPct = isReserved
    ? 100
    : targetPct != null
      ? targetPct
      : group.spent_pct != null
        ? Number(group.spent_pct)
        : 0;

  const categoriesOverspend = isReserved
    ? categories.reduce((sum, cat) => {
        const spent = Number(cat.spent_eur ?? 0);
        const budget = cat.budget_eur != null ? Number(cat.budget_eur) : 0;
        return sum + Math.max(0, spent - budget);
      }, 0)
    : 0;
  const unassigned = isReserved
    ? Math.max(
        0,
        Number(
          group.envelope_unassigned_eur ??
            Math.max(
              Number(group.target_budget_eur ?? 0) -
                Number(group.envelope_detail_eur ?? group.categories_budget_sum_eur ?? 0),
              0,
            ),
        ) - categoriesOverspend,
      )
    : Math.max(
        0,
        Number(group.target_budget_eur ?? 0) - Number(group.categories_budget_sum_eur ?? 0),
      );

  const hasDetail = categories.length > 0 || unassigned > 0;
  const Chevron = expanded ? CaretUp : CaretDown;

  return (
    <section
      data-ds-id={
        expanded ? 'ds/card/expense-group--aberto' : 'ds/card/expense-group--fechado'
      }
      className={cn(
        'flex w-full min-w-0 flex-col overflow-hidden rounded-ds-lg border bg-card',
        expanded || isAtRisk ? 'border-sage-border' : 'border-border',
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full min-w-0 items-center gap-space-3 px-space-5 py-space-4 text-left transition-colors hover:bg-muted/50"
      >
        <span
          className="h-2 w-2 shrink-0 rounded-ds-full"
          style={{ backgroundColor: color }}
          aria-hidden
        />
        <span className="type-title-card min-w-0 flex-1 truncate font-display text-card-foreground">
          {groupLabel}
        </span>
        <span className="type-body-sm tnum shrink-0 font-display font-semibold text-card-foreground">
          {hasIncome && header.secondary != null
            ? `${formatEUR(header.primary)} / ${formatEUR(header.secondary)}`
            : formatEUR(header.primary)}
        </span>
        {exceededCount > 0 ? (
          <span
            className="h-2 w-2 shrink-0 rounded-ds-full bg-danger-fg"
            role="img"
            aria-label={
              exceededCount === 1
                ? pt.expenses.byGroup.groupOverBudgetOne
                : formatGuidedFlowTemplate(pt.expenses.byGroup.groupOverBudgetMany, {
                    count: exceededCount,
                  })
            }
            title={
              exceededCount === 1
                ? pt.expenses.byGroup.groupOverBudgetOne
                : formatGuidedFlowTemplate(pt.expenses.byGroup.groupOverBudgetMany, {
                    count: exceededCount,
                  })
            }
          />
        ) : null}
        <Chevron className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      </button>

      {showBar ? (
        <div className="px-space-5 pb-space-4">
          <GroupProgressTrack
            percentage={barPct}
            color={color}
            gradient
            ariaLabel={
              isReserved ? pt.budget.method.groupReservedVsTarget : groupLabel
            }
          />
        </div>
      ) : null}

      {/* `.pen` › `error` — tom warning, não danger */}
      {isError ? (
        <>
          <div className="h-px w-full bg-border" aria-hidden />
          <CardErrorState title={pt.expenses.byGroup.loadError} onRetry={onRetry} />
        </>
      ) : null}

      {!isError && expanded && hasDetail ? (
        <>
          <div className="h-px w-full bg-border" aria-hidden />
          <ul className="flex min-w-0 flex-col gap-space-3 px-space-5 pb-space-4 pt-space-3">
            {categories.map((cat) => (
              <CategorySpendRow
                key={cat.pot_id || cat.category_id}
                name={cat.category_name}
                spent={Number(cat.spent_eur ?? 0)}
                budget={cat.budget_eur != null ? Number(cat.budget_eur) : null}
                color={cat.category_color || DEFAULT_GROUP_COLOR}
                status={cat.status}
              />
            ))}
            {unassigned > 0 ? (
              <CategorySpendRow
                name={
                  isReserved
                    ? pt.expenses.byGroup.reservedUnassigned
                    : pt.expenses.byGroup.unassigned
                }
                spent={unassigned}
                budget={null}
                color={color}
                dimmed
                testId={
                  isReserved
                    ? 'expenses-pyf-unassigned-row'
                    : 'expenses-group-unassigned-row'
                }
              />
            ) : null}
          </ul>
        </>
      ) : null}

      {!isError && expanded && !hasDetail ? (
        <>
          <div className="h-px w-full bg-border" aria-hidden />
          <p className="type-chip px-space-4 py-space-2-5 font-normal text-muted-foreground">
            {isReserved
              ? pt.expenses.byGroup.reservedEmpty
              : pt.expenses.byGroup.noCategories}
          </p>
        </>
      ) : null}
    </section>
  );
}

/** Vista «Onde está a ir» — grupos do método com subcategorias expansíveis. */
export function ExpensesByGroupList({ groups, hasIncome, isError = false, onRetry }) {
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toggle = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  if (isError) {
    return (
      <div className="w-full min-w-0 overflow-hidden rounded-ds-lg border border-border bg-card">
        <CardErrorState title={pt.expenses.byGroup.loadError} onRetry={onRetry} />
      </div>
    );
  }

  if (!groups.length) return null;

  return (
    <ul className="flex w-full min-w-0 flex-col gap-space-2-5 overflow-x-hidden">
      {groups.map((group) => {
        const id = groupKey(group);
        return (
          <li key={id} className="w-full min-w-0">
            <ExpensesGroupSection
              group={group}
              hasIncome={hasIncome}
              expanded={expandedIds.has(id)}
              onToggle={() => toggle(id)}
            />
          </li>
        );
      })}
    </ul>
  );
}
